import {Box, Button, Typography} from '@mui/material';
import {t} from 'i18next';
import {ReactComponent as ReplaceIcon} from '../../../assests/svg/project_replace_icon.svg';
import {ReactComponent as UploadIcon} from '../../../assests/svg/upload_button_icon.svg';
import {useContext, useEffect, useRef, useState, useMemo} from 'react';
import ReportDetailsPanel from '../../concierge-reports/ConciergeDrawerPanel';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  setModalDropDownSelectedItem,
  setModalStatus,
} from '../../../redux/slices/invoiceModalSlice';
import AurPopup from '../../common/aur-popup/AurPopup';
import {
  MODAL_PROJECT_DROP_DOWN_ID,
  POPUP_BUTTON_STYLE,
  PROJECT_LIST_SEARCH_ENABLE_COUNT,
} from '../../../constants/constants';
import AurClientDropdown from '../../client-management/aur-client-dropdown/AurClientDropdown';
import {getOverviewProjects} from '../../../api/UserManagementService';
import {getClientsAsLookup} from '../../../api/ReportingManagement';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {setShowUpload} from '../../../redux/slices/conciergeReportSlice';
import {AuthorizationContext} from '../../../contexts/authorizationContext/AuthorizationContext';
import {Permissions, Resources} from '../../../utils/authorization';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import {unionBy} from 'lodash';

const PROJECT_PAGE_SIZE = 100;
/**
 * Component for Client User only
 * @returns
 */
const ClientProjectDetails = ({
  clientName,
  projectName,
  projectId,
}: {
  clientName: string;
  projectName: string;
  projectId: string | undefined;
}) => (
  <>
    <Box>
      <Typography className="typo-text subhead3_size">{clientName}</Typography>
    </Box>
    <Box>
      <Typography className="typo-text display3_size">
        {projectId ?? t('conciergeReports.projectId')}{' '}
        <span className="color-divider"> | </span>
        {projectName}
      </Typography>
    </Box>
  </>
);

/**
 * Header component for concierge reports
 * @returns
 */
const ConciergeReportsHeader = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [type, setType] = useState('');
  const [dropDownProjects, setDropDownProjects] = useState<
    Array<Record<string, string>>
  >([]);
  const [initialProjectListLoading, setInitialProjectListLoading] =
    useState<boolean>(true);
  const [projectListLoading, setProjectListLoading] = useState<boolean>(false);
  const [projectListSearchEnable, setProjectListSearchEnable] =
    useState<boolean>(false);
  const pickedProjectFromDropdown = useRef<Record<string, string>>();
  const [isClientAssigned, setIsClientAssigned] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const dispatch = useDispatch();
  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient
  );
  const projects = useSelector(
    (state: RootState) => state.clientReducer[MODAL_PROJECT_DROP_DOWN_ID]
  );
  const modalStatus = useSelector(
    (state: RootState) => state.invoiceModal.modalIsOpen
  );
  const selectedProject = useSelector(
    (state: RootState) => state.invoiceModal.modalDropDownSelectedItem
  );
  const projectId = projects?.selectedOption;

  const userRole = useSelector(
    (state: RootState) => state?.analytics?.user?.role
  );

  const hasSysAdminRole = useMemo(
    () => ['admin', 'client-admin'].includes(userRole ?? ''),
    [userRole]
  );

  const projectName = projects?.options.find(
    (opt) => opt.id === projects?.selectedOption
  )?.display;

  const clientName = selectedClient?.name;
  const permissionContext = useContext(AuthorizationContext);
  const clientId = selectedClient?.clientId;
  const clientRole = useSelector(
    (state: RootState) => state.conciergeReport.isClient
  );

  const {canUploadConciergeReports} = usePermissionsByPage(
    PAGE_NAME.PROJECT_OVERVIEW,
    projectId ?? undefined
  );

  const [viewCustomsProject, setViewCustomsProject] = useState(selectedProject);

  const handleUploadReport = () => {
    setType('upload');
    setShowDrawer(true);
  };

  const prevProjectInput = useRef<{
    searchInput: string | undefined;
    page: number;
    reset: boolean;
  }>({searchInput: undefined, page: 1, reset: false});

  const fetchProjectList = async (
    searchInput?: string,
    page = 1,
    reset = false
  ) => {
    if (
      prevProjectInput.current.searchInput === searchInput &&
      prevProjectInput.current.page === page &&
      prevProjectInput.current.reset === reset
    ) {
      setInitialProjectListLoading(false);
      return;
    }

    prevProjectInput.current = {searchInput, page, reset};

    if (page === 1) {
      setProjectListLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      let queryParam = `clientId=${clientId}&pageSize=${PROJECT_PAGE_SIZE}&pageNumber=${page}`;
      if (searchInput) queryParam += `&SearchTerm=${searchInput}`;
      const body = JSON.stringify({
        branches: [],
        departments: [],
        entities: [],
      });
      const response = await getOverviewProjects(
        selectedClient.clientId,
        body,
        queryParam
      );

      const fetchedProjects = response.data.data;
      setHasMore(fetchedProjects.length === PROJECT_PAGE_SIZE);

      const newProjectList = reset
        ? fetchedProjects
        : unionBy([...dropDownProjects, ...fetchedProjects], 'projectId');

      if (
        dropDownProjects.length === 0 &&
        fetchedProjects.length >= PROJECT_LIST_SEARCH_ENABLE_COUNT &&
        !projectListSearchEnable
      ) {
        setProjectListSearchEnable(true);
      }

      setDropDownProjects(newProjectList);
    } catch (error) {
      console.error('Error fetching ECA projects', error);
    } finally {
      setProjectListLoading(false);
      setIsLoadingMore(false);
      setInitialProjectListLoading(false);
    }
  };

  const loadMoreProjects = () => {
    if (!hasMore || isLoadingMore || projectListLoading) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchProjectList(searchTerm, nextPage);
  };
  const handleProjectSearch = (input: string) => {
    setSearchTerm(input);
    setPageNumber(1);
    fetchProjectList(input, 1, true);
  };

  useEffect(() => {
    if (modalStatus && clientId) {
      setInitialProjectListLoading(true);
      fetchProjectList('', 1, true);
    }
  }, [modalStatus, clientId]);

  const handleProjectSwitch = () => {
    dispatch(setModalStatus(true));
  };

  const modalContinueHandler = () => {
    dispatch(
      setModalDropDownSelectedItem(
        projects.selectedOption && projects.selectedOptionLable
          ? {
              id: projects.selectedOption,
              name: projects.selectedOptionLable,
              code:
                dropDownProjects.filter(
                  (i) => projects.selectedOption === i.projectId
                )[0]?.projectCode || '',
            }
          : {id: '', name: '', code: ''}
      )
    );
    dispatch(setModalStatus(false));
    setViewCustomsProject({
      id: projectId ?? '',
      name: projectName,
    });
  };
  const modalCancelHandler = () => {
    pickedProjectFromDropdown.current = dropDownProjects.find(
      (item) => item.projectId === viewCustomsProject.id
    );
    dispatch(setModalStatus(false));
  };

  useEffect(() => {
    const fetchClients = async (searchTerm = '') => {
      setLoading(true);
      try {
        const result = await getClientsAsLookup(searchTerm);
        const data = result?.data?.length || 0;
        setIsClientAssigned(data > 0);
        dispatch(setShowUpload(data > 0));
      } catch (e) {
        setIsClientAssigned(false);
        dispatch(setShowUpload(false));
      } finally {
        setLoading(false);
      }
    };
    if (!clientRole) {
      fetchClients();
    }
  }, [clientRole]);

  return (
    <>
      <Box className="concierge-reports-header bg-img">
        <Box className="header-details">
          {clientRole ? (
            <ClientProjectDetails
              clientName={clientName}
              projectName={viewCustomsProject.name}
              projectId={selectedProject.code}
            />
          ) : (
            <Typography className="typo-text display3_size">
              {t('conciergeReports.title')}
            </Typography>
          )}
        </Box>
        {(permissionContext.cannot(
          Permissions.Access,
          Resources.Reports_RestrictCustomUpload
        ) ||
          (isClientAssigned &&
            canUploadConciergeReports &&
            !hasSysAdminRole)) && (
          <Button
            id="basic-button"
            startIcon={clientRole ? <ReplaceIcon aria-hidden="true" /> : <UploadIcon aria-hidden="true" />}
            className={`typo-text buttonMedium_size custom-menu-button custom-button ${!clientRole && !isClientAssigned ? 'hidden' : ''}`}
            variant="contained"
            data-testid="concierge-reports-header-btn"
            sx={{
              textTransform: 'none',
              minWidth: '120px',
              minHeight: '40px',
            }}
            onClick={clientRole ? handleProjectSwitch : handleUploadReport}
          >
            {/* Button label based on user role */}
            <Typography>
              {t(`conciergeReports.${clientRole ? 'project' : 'uploadReport'}`)}
            </Typography>
          </Button>
        )}
      </Box>
      {showDrawer && (
        <ReportDetailsPanel
          setShowDrawer={setShowDrawer}
          showDrawer={showDrawer}
          type={type}
        />
      )}
      <AurPopup
        open={modalStatus}
        onOk={modalContinueHandler}
        className="find-invoice-popup"
        onCancel={modalCancelHandler}
        onClose={modalCancelHandler}
        buttonStyle={POPUP_BUTTON_STYLE.SIDEBYSIDE}
        popupHeading={t('conciergeReports.projectSwitchModalHeading')}
        showPopupHeading={true}
        contentType="element"
        labelActionCancel={'Cancel'}
        labelActionOk={'Continue'}
        showCloseIcon={true}
        showPopupTitle={true}
        popupTitle={t('conciergeReports.projectSwitchModalTitle')}
        continueButtonState={!projects?.selectedOption}
        showOkOption={!initialProjectListLoading}
        showCancelOption={!initialProjectListLoading}
        popupContent={
          initialProjectListLoading ? (
            <AurLoadingIndicator
              className="project-switch-modal-loader"
              size={32}
              show={initialProjectListLoading}
            />
          ) : (
            <AurClientDropdown
              buttonOrTextField={'button'}
              data={dropDownProjects}
              buttonLabel="Project Name"
              dropDownID={MODAL_PROJECT_DROP_DOWN_ID}
              idKey="projectId"
              displayKey="projectName"
              buttonBorderHighlight={true}
              selectDefaultFirstItem={false}
              initialSelectedOptionId={
                pickedProjectFromDropdown.current?.projectId ??
                selectedProject.id
              }
              onSearchInput={handleProjectSearch}
              searchReqLoading={projectListLoading}
              disableDropDownForSingleItem
              enableSearch={projectListSearchEnable}
              onLoadMore={loadMoreProjects}
              isLoadingMore={isLoadingMore}
              initialLoading={projectListLoading}
            />
          )
        }
      />
      {<AurLoadingIndicator overlay={true} isOpen={loading} />}
    </>
  );
};

export default ConciergeReportsHeader;
